import { getLeaderboardEndDate, LeaderboardRewardType } from 'jigsaw';
import { useEffect, useMemo, useState } from 'react';
import { HStack, Skeleton, Text, VStack } from 'ui_2';
import { BiHourglass } from 'react-icons/bi';
import { getTimeDiffString } from '@src/data/utils';
import { Leaderboard } from '@src/data/types';
import parc from '@assets/parc.png';
import part from '@assets/part.png';

const icons = {
  [LeaderboardRewardType.PARC]: parc,
  [LeaderboardRewardType.PART]: part,
  [LeaderboardRewardType.ALEO]: undefined,
};

const PrizeItem = ({
  medal,
  amount,
  iconSrc,
  position,
}: {
  medal: string;
  amount: number;
  iconSrc?: string;
  position: string;
}) => (
  <HStack className='items-center gap-2'>
    <span className='text-xl'>{medal}</span>
    <HStack className='items-center gap-2 rounded-full bg-yellow-100 px-2'>
      {iconSrc && (
        <img src={iconSrc} alt={position} className='h-[16px] w-[16px]' />
      )}
      <Text>{amount}</Text>
    </HStack>
  </HStack>
);

const PrizesView = ({ leaderboard }: { leaderboard: Leaderboard }) => {
  if (!leaderboard.reward) return null;

  const rewards = leaderboard.reward.rewards;
  const medals = ['🥇', '🥈', '🥉'];
  const positions = ['first', 'second', 'third'] as const;
  const iconSrc = icons[leaderboard.reward.type];

  return (
    <VStack>
      <HStack className='items-center gap-4'>
        {positions.map(
          (position, index) =>
            rewards[index] !== undefined && (
              <PrizeItem
                key={position}
                medal={medals[index]}
                amount={rewards[index]}
                iconSrc={iconSrc}
                position={position}
              />
            )
        )}
      </HStack>
      {rewards[3] !== undefined && (
        <Text font='xs'>
          All other players in the top 10 will earn {rewards[3]}{' '}
          {leaderboard.reward.type.toUpperCase()}
        </Text>
      )}
    </VStack>
  );
};

type LeaderboardInfoBannerProps = {
  isLoading: boolean;
  leaderboard?: Leaderboard;
};

export const LeaderboardInfoBanner = ({
  leaderboard,
  isLoading,
}: LeaderboardInfoBannerProps) => {
  const closeDate = useMemo<Date | undefined>(() => {
    if (!leaderboard) return;
    return getLeaderboardEndDate(leaderboard.duration);
  }, [leaderboard?.duration]);

  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 500);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return;
    // return <Skeleton className='h-[90px] w-full' />;
  }

  if (!leaderboard?.reward || leaderboard.reward.rewards.length === 0)
    return null;

  return (
    <div className='flex w-full flex-col items-center rounded-lg bg-green-200 p-3'>
      <Text font='bold-md' className='mb-1 text-center'>
        Top 10 receives airdrops
      </Text>
      <VStack className='items-center gap-2'>
        <Text font='sm' className='text-center'>
          Make it into the Top 10 and earn{' '}
          {leaderboard.reward.type.toUpperCase()}
        </Text>
        <PrizesView leaderboard={leaderboard} />
        {closeDate && (
          <Text
            font='sm'
            className='flex items-center justify-center text-center'
          >
            Next Top 10 closes in{' '}
            <span className='ml-1 inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 align-middle'>
              <BiHourglass size={15} className='align-middle' />
              <span className='align-middle text-xs'>
                {getTimeDiffString(closeDate, now)}
              </span>
            </span>
          </Text>
        )}
      </VStack>
    </div>
  );
};
